import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiClient, ApiError } from "../lib/apiClient";
import { initPush } from "../lib/pushNotifications";
import { useI18n } from "../i18n/I18nProvider";
import type { Language } from "../i18n/catalog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  /** Die persistierte Profil_Sprache des Nutzers (Req 7.2, 7.4). */
  language: Language;
}

interface AuthContextValue {
  /** The currently authenticated user, or `null` when logged out. */
  user: AuthUser | null;
  /** `true` while the initial session check is in-flight. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Setzt die Aktive_Sprache (über `i18n.setLanguage`, inkl. Cache) und
   * persistiert sie bei eingeloggtem Nutzer via `PATCH /api/auth/me`
   * (Req 7.1). Der Aufruf ist optimistisch: Die Aktive_Sprache bleibt gesetzt,
   * auch wenn das Persistieren fehlschlägt.
   */
  setLanguagePreference: (lang: Language) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { setLanguage } = useI18n();

  // Check whether a valid session already exists (cookie present on the server).
  useEffect(() => {
    let cancelled = false;

    apiClient
      .get<AuthUser>("/api/auth/me", { skipAuthRedirect: true })
      .then((data) => {
        if (cancelled) return;
        setUser(data);
        // Restore the active language from the persisted profile (Req 7.2, 7.4).
        setLanguage(data.language);
      })
      .catch((err) => {
        // 401 is expected when the user is not logged in — don't log it.
        if (err instanceof ApiError && err.status === 401) return;
        console.error("Session check failed:", err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [setLanguage]);

  // Once a user is authenticated (via login or restored session), register the
  // Service Worker and subscribe to push. Fire-and-forget: `initPush` never
  // throws, so a failure here leaves the app running email-only.
  useEffect(() => {
    if (user) {
      void initPush();
    }
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    // POST /api/auth/login returns { user }, whereas GET /api/auth/me returns the
    // user fields directly. Unwrap the envelope so user.id is populated right
    // after login (otherwise owner-gated UI stays hidden until a reload).
    const data = await apiClient.post<{ user: AuthUser }>("/api/auth/login", {
      email,
      password,
    });
    setUser(data.user);
    // Adopt the profile language right after login (Req 7.2, 7.4).
    setLanguage(data.user.language);
  }, [setLanguage]);

  const setLanguagePreference = useCallback(
    async (lang: Language) => {
      // Optimistic: set the active language (and cache) immediately. This holds
      // even if persisting to the backend fails below.
      setLanguage(lang);
      // Optimistically reflect the new preference in the local user state.
      setUser((prev) => (prev ? { ...prev, language: lang } : prev));
      // Only logged-in users have a profile to persist to (Req 7.1). Anonymous
      // users keep the language in the cache only.
      if (!user) return;
      await apiClient.patch("/api/auth/me", { language: lang });
    },
    [setLanguage, user],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/api/auth/logout");
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, login, logout, setLanguagePreference }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
